import type { ClientUnit, Risk } from '../../types/api';

/** Trim + lowercase for comparing risk.country to unit.country. */
export function normalizeCountryKey(country: string | undefined | null): string {
  return (country ?? '').trim().toLowerCase();
}

/** Trim + lowercase for comparing risk.operating_mode to unit.operating_model. */
export function normalizeOperatingModelKey(operatingModel: string | undefined | null): string {
  return (operatingModel ?? '').trim().toLowerCase();
}

/** Pair key for exact country + operating model matching. */
function scopePairKey(countryKey: string, operatingModelKey: string): string {
  return `${countryKey}|${operatingModelKey}`;
}

export interface UnitScopeKeys {
  countryKeys: Set<string>;
  operatingModelKeys: Set<string>;
  countryOperatingModelPairs: Set<string>;
}

/** Distinct normalized country keys from the current client’s units. */
export function unitScopeKeysFromClientUnits(
  units: readonly Pick<ClientUnit, 'country' | 'operating_model'>[]
): UnitScopeKeys {
  const countryKeys = new Set<string>();
  const operatingModelKeys = new Set<string>();
  const countryOperatingModelPairs = new Set<string>();
  for (const u of units) {
    const countryKey = normalizeCountryKey(u.country);
    const operatingModelKey = normalizeOperatingModelKey(u.operating_model);
    if (countryKey) countryKeys.add(countryKey);
    if (operatingModelKey) operatingModelKeys.add(operatingModelKey);
    if (countryKey && operatingModelKey) {
      countryOperatingModelPairs.add(scopePairKey(countryKey, operatingModelKey));
    }
  }
  return { countryKeys, operatingModelKeys, countryOperatingModelPairs };
}

/**
 * Keeps risks in scope for My Units:
 * - always requires country match
 * - when unit operating models exist, also requires operating model match
 * - prefers exact (country + operating model) pair matching when both are present
 */
export function filterRisksByUnitScope(risks: Risk[], keys: UnitScopeKeys): Risk[] {
  if (keys.countryKeys.size === 0) return risks;
  const requireOperatingModel = keys.operatingModelKeys.size > 0;
  return risks.filter((risk) => {
    const countryKey = normalizeCountryKey(risk.country);
    if (!countryKey || !keys.countryKeys.has(countryKey)) return false;
    if (!requireOperatingModel) return true;
    const operatingModelKey = normalizeOperatingModelKey(risk.operating_mode);
    if (!operatingModelKey) return false;
    if (keys.countryOperatingModelPairs.size > 0) {
      return keys.countryOperatingModelPairs.has(scopePairKey(countryKey, operatingModelKey));
    }
    return keys.operatingModelKeys.has(operatingModelKey);
  });
}

export function isUnitScopeToggleAvailable(userIdNo: number | null | undefined): boolean {
  return typeof userIdNo === 'number' && Number.isFinite(userIdNo);
}
